import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight } from "lucide-react";

export default function Personvern() {
  return (
    <div data-testid="page-personvern">
      {/* Hero */}
      <section className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#D97B66]"></span>
            <span className="text-xs uppercase tracking-[0.25em] text-[#6B726C] font-medium">
              Juridisk
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-[#2C302E] leading-[1.05]">
            Personvern&shy;erklæring
          </h1>
          <p className="mt-6 text-base text-[#6B726C]">
            <strong>Sist oppdatert: 1. juni 2026</strong>
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="space-y-10 text-[#2C302E]">

          <div className="flex items-start gap-4 p-6 bg-[#8BA896]/10 rounded-2xl">
            <ShieldCheck className="w-6 h-6 text-[#8BA896] mt-1 flex-shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-[#2C302E] leading-relaxed m-0">
              Hos Småstund tar vi personvern på alvor. Denne erklæringen forklarer hvordan vi samler inn,
              bruker og beskytter dine personopplysninger i henhold til EUs personvernforordning (GDPR) og
              norsk personopplysningslov.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">1. Innledning</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              Småstund («vi», «oss», «vår») er opptatt av å beskytte ditt personvern. Denne
              personvernerklæringen forklarer hvordan vi samler inn, bruker og beskytter dine
              personopplysninger når du besøker vår nettside eller handler hos oss.
            </p>
            <p className="text-[#4A4F4A] leading-relaxed mt-4">
              <strong>Kontaktinformasjon:</strong>
              <br />
              E-post: <a href="mailto:post@xn--smstund-q1a.no" className="text-[#D97B66] underline">post@småstund.no</a>
              <br />
              Organisasjonsnummer: [SETT INN ORGANISASJONSNUMMER NÅR DU HAR DET]
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">2. Hvilke opplysninger samler vi inn?</h2>
            <p className="text-[#4A4F4A] leading-relaxed mb-3">Vi samler inn følgende opplysninger:</p>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li><strong>Navn og e-postadresse</strong> – for å sende deg PDF-filer og ordrebekreftelser</li>
              <li><strong>Fakturaadresse</strong> – for å utstede korrekt kvittering</li>
              <li><strong>Betalingsinformasjon</strong> – behandles sikkert av Stripe (vi lagrer ingen kortopplysninger)</li>
              <li><strong>IP-adresse og nettlesertype</strong> – for å forbedre vår nettside og forhindre misbruk</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">3. Hvordan bruker vi opplysningene?</h2>
            <p className="text-[#4A4F4A] leading-relaxed mb-3">Dine opplysninger brukes til:</p>
            <ul className="list-none pl-0 space-y-2 text-[#4A4F4A]">
              <li>✅ Å levere PDF-filene du har kjøpt (e-post)</li>
              <li>✅ Å sende ordrebekreftelse og kvittering</li>
              <li>✅ Å gi deg kundeservice</li>
              <li>✅ Å forbedre våre produkter og tjenester</li>
              <li>❌ Vi selger ALDRI dine opplysninger til tredjeparter</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">4. Lagring av opplysninger</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li><strong>E-post og navn</strong> lagres i 36 måneder (for å hjelpe deg med å gjenopprette gamle kjøp)</li>
              <li><strong>Betalingsinformasjon</strong> lagres KUN hos Stripe – vi har ikke tilgang til kortnumre</li>
              <li>Du kan når som helst be om sletting ved å kontakte oss</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">5. Dine rettigheter</h2>
            <p className="text-[#4A4F4A] leading-relaxed mb-3">Du har rett til å:</p>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li>Be om innsyn i hvilke opplysninger vi har om deg</li>
              <li>Be om retting eller sletting av dine opplysninger</li>
              <li>Trekke tilbake samtykke når som helst</li>
              <li>
                Klage til Datatilsynet (
                <a href="https://www.datatilsynet.no" target="_blank" rel="noopener noreferrer" className="text-[#D97B66] underline">
                  www.datatilsynet.no
                </a>
                )
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">6. Sikkerhet</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              Vi bruker SSL-kryptering (HTTPS) og sikre API-tilkoblinger. Alle betalinger håndteres av
              Stripe som er PCI DSS-sertifisert.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">7. Endringer i erklæringen</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              Vi kan oppdatere denne erklæringen. Siste versjon vil alltid finnes på denne siden.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">8. Kontakt oss</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              For spørsmål om personvern, send e-post til:{" "}
              <a href="mailto:post@xn--smstund-q1a.no" className="text-[#D97B66] underline">post@småstund.no</a>
            </p>
          </div>

        </div>

        {/* Back link */}
        <div className="mt-16 pt-10 border-t border-[#E5E2DC]">
          <Link
            to="/"
            data-testid="personvern-back-home"
            className="inline-flex items-center gap-2 text-[#D97B66] hover:text-[#C66A55] font-medium"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Tilbake til forsiden
          </Link>
        </div>
      </section>
    </div>
  );
}
